import { useState, useEffect, useCallback } from 'react';
import { offlineBookmarkService } from '../services/offlineBookmarkService';
import { syncService } from '../services/syncService';
import { networkService } from '../services/networkService';
import { useAuth } from '../contexts/AuthContext';
import { bookmarkService as firebaseBookmarkService } from '../services/bookmarkService.js'; // Corrected import

export const useBookmarks = () => {
  const { user, isAuthenticated } = useAuth();
  const [bookmarks, setBookmarks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isOnline, setIsOnline] = useState(networkService.getNetworkStatus());
  const [syncStatus, setSyncStatus] = useState(null);

  // Add bookmark
  const addBookmark = async (bookmarkData) => {
    if (!user?.uid) {
      throw new Error('User not authenticated');
    }
    
    setError(null);
    try {
      // 1. Prevent adding a bookmark if the URL already exists (client-side check first)
      const existingLocally = bookmarks.find(b => b.url === bookmarkData.url);
      if (existingLocally) {
        const errMessage = 'Bookmark with this URL already exists.';
        console.warn(errMessage, existingLocally);
        setError(errMessage);
        // No need to throw here if offlineBookmarkService will also check server-side
        // and provide a more specific error if needed.
        // We can let offlineBookmarkService handle the ultimate decision and error throwing.
        // return; // Or throw if we want to stop immediately based on local check
      }

      // offlineBookmarkService.addBookmark will now also check on the server if online.
      const newBookmark = await offlineBookmarkService.addBookmark(bookmarkData, user.uid);
      
      // 2. Optimistic update, ensuring no ID collision locally
      setBookmarks(prev => {
        const filteredPrev = prev.filter(b => b.id !== newBookmark.id);
        return [newBookmark, ...filteredPrev];
      });

      // Helper to check if the title is the default "Bookmark from [domain]"
      const isDefaultTitle = (title, urlString) => {
        if (!title || !urlString) return false;
        try {
          const urlObj = new URL(urlString);
          // Normalize hostname by removing 'www.' if present, for more robust matching
          const hostname = urlObj.hostname.replace(/^www\\\\./, '');
          const expectedDefaultTitle = `Bookmark from ${hostname}`;
          // Also check against the version with 'www.' if the original title might contain it
          const expectedDefaultTitleWithWww = `Bookmark from www.${hostname}`;
          return title === expectedDefaultTitle || title === expectedDefaultTitleWithWww;
        } catch (e) {
          console.warn('Invalid URL provided for default title check:', urlString, e);
          return false; // If URL is invalid, cannot determine default title
        }
      };

      // Call metadata extraction API if title is missing or is the default one
      const shouldFetchMetadata = !bookmarkData.title || isDefaultTitle(bookmarkData.title, bookmarkData.url);

      if (shouldFetchMetadata && newBookmark.url && newBookmark.id) {
        try {
          const token = await user.getIdToken();
          const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/metadata/extract-metadata`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              url: newBookmark.url,
              bookmarkId: newBookmark.id
            })
          });

          if (!response.ok) {
            const errorData = await response.json();
            console.error('❌ Error fetching metadata:', response.status, errorData);
          } else {
            const result = await response.json();
            // console.log('✅ Metadata extraction API call successful:', result);
            // No need to update local state here, as Firestore listener should pick up changes
          }
        } catch (apiError) {
          console.error('❌ Failed to call metadata extraction API:', apiError);
        }
      }

      return newBookmark;
    } catch (err) {
      setError(isOnline ? 'Failed to add bookmark' : 'Bookmark saved offline - will sync when online');
      throw err;
    }
  };

  // Delete bookmark
  const deleteBookmark = async (id) => {
    if (!user?.uid) {
      throw new Error('User not authenticated');
    }
    
    setError(null);
    try {
      await offlineBookmarkService.deleteBookmark(id, user.uid);
      setBookmarks(prev => prev.filter(bookmark => bookmark.id !== id));
    } catch (err) {
      setError(isOnline ? 'Failed to delete bookmark' : 'Bookmark deleted offline - will sync when online');
      throw err;
    }
  };

  // Update bookmark
  const updateBookmark = async (id, updates) => {
    if (!user?.uid) {
      throw new Error('User not authenticated');
    }
    
    setError(null);
    try {
      const updatedBookmark = await offlineBookmarkService.updateBookmark(id, updates, user.uid);
      setBookmarks(prev => 
        prev.map(bookmark => 
          bookmark.id === id ? { ...bookmark, ...updates } : bookmark
        )
      );
      return updatedBookmark;
    } catch (err) {
      setError(isOnline ? 'Failed to update bookmark' : 'Bookmark updated offline - will sync when online');
      throw err;
    }
  };

  // Search bookmarks locally (client-side filtering)
  const searchBookmarks = async (searchTerm) => {
    if (!user?.uid) return [];
    
    try {
      return await offlineBookmarkService.searchBookmarks(searchTerm, user.uid);
    } catch (err) {
      console.error('Search failed:', err);
      return bookmarks.filter(bookmark => 
        bookmark.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        bookmark.url?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        bookmark.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        bookmark.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
  };

  // Force refresh from server
  // const refreshBookmarks = async () => {
  //   if (!user?.uid) return;
    
  //   setLoading(true);
  //   setError(null);
  //   try {
  //     const data = await offlineBookmarkService.refreshBookmarks(user.uid);
  //     setBookmarks(data);
  //   } catch (err) {
  //     setError(isOnline ? 'Failed to refresh bookmarks' : 'Cannot refresh while offline');
  //     console.error(err);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const loadLocalBookmarks = useCallback(async () => {
    if (!user?.uid) return;
    // console.log('🔄 Attempting to load bookmarks from local store for user (offline refresh path):', user.uid);
    setLoading(true);
    setError(null);
    try {
      const data = await offlineBookmarkService.refreshBookmarks(user.uid);
      // De-duplicate based on bookmark ID, similar to Firestore snapshot handling
      const uniqueBookmarks = Array.from(new Map(data.map(b => [b.id, b])).values());
      setBookmarks(uniqueBookmarks);
      // console.log('📚 Loaded', uniqueBookmarks.length, 'bookmarks from local store.');
    } catch (err) {
      console.error('❌ Failed to load bookmarks from local store (offline refresh path):', err);
      setError('Failed to refresh bookmarks from local store. Ensure you are online to sync.');
    } finally {
      setLoading(false);
    }
  }, [user?.uid, setLoading, setError, setBookmarks]);

  // Load bookmarks when user authentication state changes
  useEffect(() => {
    // console.log('📚 useBookmarks: Auth state changed - isAuthenticated:', isAuthenticated, 'user.uid:', user?.uid);
    if (isAuthenticated && user?.uid) {
      // console.log('📚 useBookmarks: Setting up Firestore listener for user', user.uid);
      syncService.setCurrentUser(user.uid);

      // Set up Firestore real-time listener
      const unsubscribe = firebaseBookmarkService.onBookmarksSnapshot(
        user.uid,
        (serverBookmarks) => {
          // console.log(' Firestore Snapshot Received:', serverBookmarks.length, 'bookmarks');
          setLoading(true); // Indicate loading while processing snapshot
          // De-duplicate based on bookmark ID
          const uniqueBookmarks = Array.from(new Map(serverBookmarks.map(b => [b.id, b])).values());
          if (serverBookmarks.length !== uniqueBookmarks.length) {
            console.warn(`Snapshot: ${serverBookmarks.length} bookmarks, ${uniqueBookmarks.length} unique. Duplicates removed.`);
          }
          setBookmarks(uniqueBookmarks);
          setLoading(false);
          setError(null); // Clear any previous errors on successful update

          // Optionally, update offline storage after receiving snapshot to keep it in sync
          // This can be intensive if snapshots are frequent or large.
          // Consider if syncService already handles this adequately.
          // offlineStorageService.saveBookmarks(uniqueBookmarks, user.uid)
          //   .then(() => console.log('Offline storage updated from snapshot'))
          //   .catch(err => console.error('Error updating offline storage from snapshot:', err));
        },
        (err) => {
          console.error('❌ Error from Firestore listener:', err);
          setError('Failed to listen for bookmark updates');
          setLoading(false);
        }
      );

      // If user is authenticated and network is online, schedule an initial sync
      // to ensure local cache is populated soon after login.
      if (networkService.getNetworkStatus()) {
        // console.log(`📚 useBookmarks: User ${user.uid} authenticated while online, scheduling initial sync.`);
        syncService.scheduleInitialSync(user.uid);
      }
      
      return () => {
        // console.log('📚 useBookmarks: Cleaning up Firestore listener for user', user.uid);
        unsubscribe(); // Detach listener when component unmounts or user changes
      };
    } else {
      // Clear bookmarks and current user in syncService when user signs out
      // console.log('📚 useBookmarks: Clearing bookmarks and syncService user (user not authenticated or signed out)');
      setBookmarks([]);
      syncService.setCurrentUser(null);
    }
  }, [isAuthenticated, user?.uid]); // Removed loadBookmarks from dependencies as listener handles updates

  // Listen for network status changes
  useEffect(() => {
    const unsubscribe = networkService.addNetworkListener((online) => {
      setIsOnline(online);
      // Set current user for sync service when online, and potentially trigger sync via network listener in syncService
      if (user?.uid) { // Ensure user is still known
        syncService.setCurrentUser(user.uid);
        if (online) {
          // The network listener in syncService itself will handle triggering syncUserData
          // if conditions (debounce, not syncing, currentUserId set) are met.
          // We can also consider calling scheduleInitialSync here if coming online with a user.
          // console.log(`📚 useBookmarks: Network came online. User ${user.uid} is active. syncService network listener will handle sync.`);
          // Optionally, to be more proactive if cache might be stale after being offline:
          // syncService.scheduleInitialSync(user.uid);
        }
      }
    });

    return unsubscribe;
  }, [user?.uid]); // Rerun if user changes, so setCurrentUser gets the right UID

  // Listen for sync status changes
  useEffect(() => {
    const unsubscribe = syncService.addSyncListener((status) => {
      setSyncStatus(status);
      
      // No longer need to reload bookmarks here, as the Firestore listener will handle updates.
      // if (status.type === 'success' && user?.uid) {
      //   loadBookmarks();
      // }
    });

    return unsubscribe;
  }, [user?.uid]); // Removed loadBookmarks from dependencies

  // Function to trigger metadata extraction for a specific bookmark
  const triggerMetadataExtraction = useCallback(async (bookmarkId, url) => {
    if (!user || !user.uid) {
      console.error('User not authenticated for metadata extraction');
      throw new Error('User not authenticated');
    }
    if (!url || !bookmarkId) {
      console.error('URL and Bookmark ID are required for metadata extraction');
      throw new Error('URL and Bookmark ID are required');
    }

    // console.log(`🚀 Triggering metadata extraction for bookmarkId: ${bookmarkId}, URL: ${url}`);
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/metadata/extract-metadata`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          url: url,
          bookmarkId: bookmarkId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ Error calling metadata extraction API:', response.status, errorData);
        throw new Error(errorData.message || 'Failed to extract metadata');
      }

      const result = await response.json();
      // console.log('✅ Metadata extraction API call successful:', result);
      // Firestore listener will handle UI updates.
      return result;
    } catch (apiError) {
      console.error('❌ Failed to call metadata extraction API:', apiError);
      throw apiError;
    }
  }, [user]);

  return {
    bookmarks,
    loading,
    error,
    isOnline,
    syncStatus,
    addBookmark,
    deleteBookmark,
    updateBookmark,
    searchBookmarks,
    triggerMetadataExtraction, // Expose the new function
    // refreshBookmarks: loadBookmarks // loadBookmarks is no longer the primary way to refresh
    refreshBookmarks: () => {
      if (user?.uid && networkService.getNetworkStatus()) {
        // console.log('🔄 Manually triggering sync/refresh for user:', user.uid);
        syncService.scheduleInitialSync(user.uid); // Or a more direct refresh if available
      } else if (user?.uid) {
        // If offline, we might just re-read from local cache
        // console.log('🔄 Attempting to reload from local cache (offline refresh attempt)');
        loadLocalBookmarks(); // Call the new function to load from local cache
      }
    }
  };
};
